import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import type { MessageCountryPrice, MessagePriceInput, MessagePriceRow, MessagePrices, RatedLine } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, ReasonField, reasonOk } from '@/components/ops/Forms';
import { Section } from '@/components/ops/Layout';
import { ConfirmDialog } from '@/components/ops/Modal';
import { MonthSelect } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatCredits, isValidScale, parseDisplayedCredits, scaleText } from '@/ops/credits';
import { currentPeriod, formatNumber, formatTimestamp } from '@/ops/format';
import { errorCode, LIVE } from '@/ops/live';

type Product = 'sms' | 'whatsapp';

/** Each message type in words: what the owner calls it, and what it is for. */
export const MESSAGE_TYPES: Record<Product, { type: string; name: string; hint: string }[]> = {
  sms: [
    { type: 'transactional', name: 'Normal', hint: 'Order updates, reminders and notifications.' },
    { type: 'marketing', name: 'Marketing', hint: 'Offers and campaigns. Quiet hours and opt-outs apply.' },
    { type: 'otp', name: 'Verification code (OTP)', hint: 'One-time passwords.' },
  ],
  whatsapp: [
    { type: 'service', name: 'Service (in-chat reply)', hint: 'Replies inside a chat the customer started, within 24 hours.' },
    { type: 'utility', name: 'Utility', hint: 'Order and account updates sent as a template.' },
    { type: 'marketing', name: 'Marketing', hint: 'Promotions sent as a template.' },
    { type: 'authentication', name: 'Authentication', hint: 'Verification codes sent as a template.' },
  ],
};

/** Provider-verify (`sms.verify.<channel>`): the provider sends and checks the code itself, charged per verification. */
const VERIFY_TYPES: Record<string, { name: string; hint: string }> = {
  sms: { name: 'Provider verification by SMS', hint: 'The provider sends and checks the code; charged per verification.' },
  whatsapp: { name: 'Provider verification by WhatsApp', hint: 'The provider sends and checks the code over WhatsApp; charged per verification.' },
};

const PRODUCT_TEXT: Record<Product, { title: string; unit: string; note: string }> = {
  sms: {
    title: 'SMS',
    unit: 'per SMS part',
    note: 'One part is up to 160 characters (70 in Arabic); a longer SMS is charged for each part.',
  },
  whatsapp: {
    title: 'WhatsApp',
    unit: 'per message',
    note: 'Meta decides the type of a template when it approves it; replies inside the 24-hour window are service messages.',
  },
};

const SOURCE_TEXT: Record<string, { label: string; tone: 'info' | 'muted' | 'warning' | 'danger' }> = {
  book: { label: 'set here', tone: 'info' },
  default: { label: 'cost + markup', tone: 'muted' },
  cost_markup: { label: 'cost + markup', tone: 'muted' },
  override: { label: "tenant's own price", tone: 'warning' },
  unpriced: { label: 'not priced', tone: 'danger' },
};

const isVerify = (row: MessagePriceRow) => row.unit === 'verification' || row.sku.startsWith('sms.verify.');

export function messageTypeName(product: Product, type: string, unit?: string): string {
  if (unit === 'verification') return VERIFY_TYPES[type]?.name ?? `Verification (${type})`;
  return MESSAGE_TYPES[product].find((t) => t.type === type)?.name ?? type;
}

function rowName(row: MessagePriceRow): string {
  return messageTypeName(row.product, row.type, isVerify(row) ? 'verification' : row.unit);
}

function rowHint(row: MessagePriceRow): string | undefined {
  return isVerify(row) ? VERIFY_TYPES[row.type]?.hint : MESSAGE_TYPES[row.product].find((t) => t.type === row.type)?.hint;
}

function unitText(row: MessagePriceRow): string {
  return isVerify(row) ? 'per verification' : PRODUCT_TEXT[row.product].unit;
}

const pricesKey = (env: string, tenantId?: string) => ['ops', 'billing', env, 'message-prices', tenantId ?? 'platform'] as const;

function SourceChip({ source }: { source: string }) {
  const text = SOURCE_TEXT[source] ?? { label: source, tone: 'muted' as const };
  return <StatusChip status={text.label} tone={text.tone} />;
}

/** Margin in credits, and as a share over our cost. A loss is shown in red, never hidden. */
function Margin({ margin, cost, scale }: { margin: number | null | undefined; cost: number | null | undefined; scale: number | null }) {
  if (margin === null || margin === undefined || cost === null || cost === undefined) return <span className="muted">—</span>;
  const over = cost > 0 ? Math.round((margin / cost) * 100) : null;
  return (
    <span style={margin < 0 ? { color: 'var(--error)', fontWeight: 600 } : undefined}>
      {formatCredits(margin, scale)}
      {over !== null && <span className="t-caption muted"> ({over > 0 ? '+' : ''}{over}% over cost)</span>}
    </span>
  );
}

/** `sku@country` — one editable price: the any-country row (`*`) or one destination. */
const priceKey = (sku: string, country: string) => `${sku}@${country}`;

interface PlannedChange {
  key: string;
  row: MessagePriceRow;
  country: string;
  /** Micro-credits, or null = remove the book row (back to cost + markup). */
  price: number | null;
  before: number | null;
  /** Our cost the below-cost check compares with: `max_cost_credits` for `*`, the country's cost otherwise. */
  threshold: number | null;
}

/** True when a new price is under our cost for what it covers (never for a revert, never when the cost is unknown). */
export function isBelowCost(change: Pick<PlannedChange, 'price' | 'threshold'>): boolean {
  return change.price !== null && change.threshold !== null && change.price < change.threshold;
}

/** The highest cost an any-country price covers (C10 `max_cost_credits`; older billing: the type's cost). */
function anyCountryThreshold(row: MessagePriceRow): number | null {
  return row.max_cost_credits ?? row.cost_credits ?? null;
}

/**
 * Pricing ▸ Message prices: the price of each SMS and WhatsApp message type for every tenant, typed in credits — for every
 * country at once (`*`) and per destination country (money review C10). Saving publishes a new price-book version now (the
 * version in force with these rows changed), so history never moves. A price below our cost needs an explicit "allow
 * below cost"; "revert to cost + markup" removes the book row.
 */
export function MessagePricesPanel({ env }: { env: string }) {
  const queryClient = useQueryClient();
  // Our cost and margin ride this read (costs:read): never fired for a role that would get a 403.
  const prices = useQuery({
    queryKey: pricesKey(env),
    queryFn: () => billingApi.messagePrices(env),
    enabled: hasPermission(PERMISSIONS.costsRead),
    ...LIVE,
  });
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [reverts, setReverts] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Record<string, string[]>>({});
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [allowBelow, setAllowBelow] = useState(false);

  const reset = () => {
    setDrafts({});
    setReverts(new Set());
    setAdded({});
    setReason('');
    setAllowBelow(false);
    setConfirming(false);
  };

  const save = useMutation({
    mutationFn: (inputs: MessagePriceInput[]) => billingApi.setMessagePrices(env, inputs, reason.trim()),
    onSuccess: (data) => {
      queryClient.setQueryData(pricesKey(env), data);
      queryClient.invalidateQueries({ queryKey: ['ops', 'billing', env] });
      reset();
    },
  });
  const serverSaysBelowCost = errorCode(save.error) === 'PRICE_BELOW_COST';

  const toggle = (sku: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(sku)) next.delete(sku);
    else next.add(sku);
    return next;
  });
  const toggleRevert = (key: string) => setReverts((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  return (
    <RequirePermission permission={PERMISSIONS.costsRead} what="Message prices">
      <QueryState query={prices} service="billing">
        {(data) => {
          const scale = data.credit_display_scale;
          const scaleKnown = isValidScale(scale);
          const rows = [...data.sms, ...data.whatsapp];

          // Every typed or reverted price → a planned change (or an error that blocks the save).
          const errors: Record<string, string> = {};
          const changes: PlannedChange[] = [];
          for (const row of rows) {
            const countries: { country: string; current: MessageCountryPrice | null }[] = [
              { country: '*', current: null },
              ...row.country_prices.map((c) => ({ country: c.country, current: c })),
              // A country typed twice, or one that already has its own row, is edited once (on its row).
              ...[...new Set(added[row.sku] ?? [])]
                .filter((c) => !row.country_prices.some((p) => p.country === c))
                .map((c) => ({ country: c, current: null })),
            ];
            for (const { country, current } of countries) {
              const key = priceKey(row.sku, country);
              const before = country === '*' ? row.price_credits : current?.price_credits ?? null;
              const threshold = country === '*' ? anyCountryThreshold(row) : current?.cost_credits ?? null;
              if (reverts.has(key)) {
                changes.push({ key, row, country, price: null, before, threshold });
                continue;
              }
              const text = drafts[key] ?? '';
              if (text.trim() === '') continue;
              const parsed = parseDisplayedCredits(text, scale);
              if (!parsed.ok) {
                errors[key] = parsed.error;
                continue;
              }
              if (parsed.micro !== before || (country !== '*' && current?.source !== 'book')) {
                changes.push({ key, row, country, price: parsed.micro, before, threshold });
              }
            }
          }
          const addedErrors = Object.entries(added).flatMap(([sku, list]) => list.filter((c, i) =>
            !/^[A-Z]{2}$/.test(c) || list.indexOf(c) !== i
            || rows.find((r) => r.sku === sku)?.country_prices.some((p) => p.country === c)));
          const hasErrors = Object.keys(errors).length > 0 || addedErrors.length > 0;
          const belowCost = changes.filter(isBelowCost);
          const needsAllow = belowCost.length > 0 || serverSaysBelowCost;
          const blocked = data.scheduled_version_from !== null;

          const priceInput = (row: MessagePriceRow, country: string, current: number | null, label: string) => {
            const key = priceKey(row.sku, country);
            const reverting = reverts.has(key);
            return (
              <>
                <input
                  className="input"
                  aria-label={label}
                  inputMode="decimal"
                  value={reverting ? '' : drafts[key] ?? ''}
                  placeholder={reverting ? 'cost + markup' : current === null ? 'e.g. 5' : formatCredits(current, scale, { unit: false })}
                  disabled={blocked || !scaleKnown || reverting}
                  aria-invalid={errors[key] ? true : undefined}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                />
                {errors[key] && <div className="field-error t-caption" style={{ color: 'var(--error)' }}>{errors[key]}</div>}
              </>
            );
          };

          const revertButton = (row: MessagePriceRow, country: string, what: string) => {
            const key = priceKey(row.sku, country);
            const on = reverts.has(key);
            return (
              <button type="button" className={`btn btn-ghost btn-sm${on ? ' is-on' : ''}`} aria-pressed={on} disabled={blocked}
                aria-label={`${on ? 'Keep the price for' : 'Revert to cost + markup:'} ${what}`}
                onClick={() => toggleRevert(key)}>
                {on ? 'Keep this price' : 'Revert to cost + markup'}
              </button>
            );
          };

          const section = (title: string, meta: string, list: MessagePriceRow[]) => list.length === 0 ? null : (
            <Section key={title} title={title} meta={meta} flush>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Message type · country</th>
                      <th className="num">Price now</th>
                      <th className="num">Our cost</th>
                      <th className="num">Margin</th>
                      {canManage && <th>New price (credits)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => {
                      const name = rowName(row);
                      const product = PRODUCT_TEXT[row.product].title;
                      const expanded = open.has(row.sku);
                      const extra = added[row.sku] ?? [];
                      const maxCost = anyCountryThreshold(row);
                      return (
                        <Fragment key={row.sku}>
                          <tr>
                            <td>
                              <div style={{ fontWeight: 600 }}>{name}</div>
                              <div className="t-caption muted">{rowHint(row)} · {unitText(row)} · every country</div>
                              {row.has_cost_card === false && (
                                <div className="t-caption" role="note" style={{ color: 'var(--warning, #b45309)' }}>
                                  No cost card — our cost is unknown, and “cost + markup” cannot price this type. Add one under Cost cards.
                                </div>
                              )}
                              <button type="button" className="linklike t-caption" aria-expanded={expanded} onClick={() => toggle(row.sku)}>
                                {expanded ? 'Hide countries' : `Countries (${row.country_prices.length + extra.length})`}
                              </button>
                            </td>
                            <td className="num">
                              <div>{row.price_credits === null ? '—' : formatCredits(row.price_credits, scale)}</div>
                              <SourceChip source={row.source} />
                            </td>
                            <td className="num">
                              {row.cost_credits === null ? <span className="muted">no cost card</span> : formatCredits(row.cost_credits, scale)}
                              {maxCost !== null && maxCost !== row.cost_credits && (
                                <div className="t-caption muted">highest {formatCredits(maxCost, scale)}</div>
                              )}
                            </td>
                            <td className="num"><Margin margin={row.margin_credits} cost={row.cost_credits} scale={scale} /></td>
                            {canManage && (
                              <td>
                                {priceInput(row, '*', row.price_credits, `New price for ${product} ${name}`)}
                                {(row.source === 'book' || row.book_price_credits !== null) && revertButton(row, '*', `${product} ${name}, every country`)}
                              </td>
                            )}
                          </tr>
                          {expanded && row.country_prices.map((c) => (
                            <tr key={`${row.sku}-${c.country}`} className="row-sub">
                              <td style={{ paddingInlineStart: 32 }}>
                                <span className="mono">{c.country}</span> <span className="t-caption muted">{name}</span>
                              </td>
                              <td className="num">
                                <div>{c.price_credits === null ? '—' : formatCredits(c.price_credits, scale)}</div>
                                <SourceChip source={c.source} />
                              </td>
                              <td className="num">{c.cost_credits === null || c.cost_credits === undefined ? <span className="muted">no cost card</span> : formatCredits(c.cost_credits, scale)}</td>
                              <td className="num"><Margin margin={c.margin_credits} cost={c.cost_credits} scale={scale} /></td>
                              {canManage && (
                                <td>
                                  {priceInput(row, c.country, c.price_credits, `New price for ${product} ${name} in ${c.country}`)}
                                  {(c.source === 'book' || (c.book_price_credits ?? null) !== null) && revertButton(row, c.country, `${product} ${name} in ${c.country}`)}
                                </td>
                              )}
                            </tr>
                          ))}
                          {expanded && extra.map((country, index) => (
                            <tr key={`${row.sku}-new-${index}`} className="row-sub">
                              <td style={{ paddingInlineStart: 32 }}>
                                <input className="input input-inline mono" aria-label={`Country for a new ${product} ${name} price`} value={country}
                                  placeholder="SA" maxLength={2}
                                  onChange={(e) => setAdded((prev) => ({
                                    ...prev,
                                    [row.sku]: (prev[row.sku] ?? []).map((v, i) => (i === index ? e.target.value.toUpperCase() : v)),
                                  }))} />
                                {country !== '' && !/^[A-Z]{2}$/.test(country) && <div className="t-caption note-error">Two letters (ISO 3166).</div>}
                                {(row.country_prices.some((p) => p.country === country) || extra.indexOf(country) !== index) && country !== '' && (
                                  <div className="t-caption note-error">{country} already has a row — edit it there.</div>
                                )}
                              </td>
                              <td className="num muted">new</td>
                              <td className="num muted">—</td>
                              <td className="num muted">—</td>
                              <td>{/^[A-Z]{2}$/.test(country) ? priceInput(row, country, null, `New price for ${product} ${name} in ${country}`) : null}</td>
                            </tr>
                          ))}
                          {expanded && canManage && (
                            <tr className="row-sub">
                              <td colSpan={5} style={{ paddingInlineStart: 32 }}>
                                <button type="button" className="btn btn-ghost btn-sm" disabled={blocked || !scaleKnown}
                                  onClick={() => setAdded((prev) => ({ ...prev, [row.sku]: [...(prev[row.sku] ?? []), ''] }))}>
                                  Set a price for one country
                                </button>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          );

          return (
            <div className="stack">
              <div className="card card-body t-body">
                <p style={{ margin: 0 }}>
                  Set what each type of message costs a tenant, in credits — for every country at once, or for one destination
                  country. A country&apos;s own price wins there; a tenant&apos;s own price wins over both. Without a price set here a
                  country is priced at our cost there plus the markup. Saving publishes a new price-book version immediately;
                  sending picks it up within a few minutes.
                </p>
                <p className="t-caption muted" style={{ margin: '8px 0 0' }}>
                  1 credit = {scaleText(scale)} micro-credits. Price book in force:{' '}
                  {data.price_book_effective_from ? `since ${formatTimestamp(data.price_book_effective_from)}` : 'none'}.
                </p>
                {!scaleKnown && (
                  <p className="t-body" role="alert" style={{ margin: '8px 0 0', color: 'var(--error)' }}>
                    The credit display scale is unknown (billing did not say), so figures are shown in micro-credits and prices
                    cannot be typed until it is known.
                  </p>
                )}
                {blocked && (
                  <p className="t-body" role="alert" style={{ margin: '8px 0 0', color: 'var(--error)' }}>
                    A price-book version takes effect on {formatTimestamp(data.scheduled_version_from)} and would replace a change made
                    now. Edit that version in the Price book tab instead.
                  </p>
                )}
                {data.open_drafts > 0 && (
                  <p className="t-caption" style={{ margin: '8px 0 0' }}>
                    {data.open_drafts === 1 ? 'An unpublished price-book draft exists' : `${data.open_drafts} unpublished price-book drafts exist`}:
                    publishing one later uses the prices it holds, not these.
                  </p>
                )}
              </div>

              {section(`${PRODUCT_TEXT.sms.title} prices`, PRODUCT_TEXT.sms.note, data.sms.filter((r) => !isVerify(r)))}
              {section(`${PRODUCT_TEXT.whatsapp.title} prices`, PRODUCT_TEXT.whatsapp.note, data.whatsapp.filter((r) => !isVerify(r)))}
              {section('Provider verification prices', 'The provider sends and checks the code itself (OTP via the provider); charged per verification.',
                rows.filter(isVerify))}

              {canManage && (
                <div className="form-actions">
                  <button type="button" className="btn btn-primary" disabled={blocked || hasErrors || changes.length === 0}
                    onClick={() => setConfirming(true)}>
                    {changes.length === 0 ? 'Save prices' : `Save ${changes.length} price${changes.length === 1 ? '' : 's'}`}
                  </button>
                  {(Object.keys(drafts).length > 0 || reverts.size > 0 || Object.keys(added).length > 0) && (
                    <button type="button" className="btn btn-ghost" onClick={reset}>Clear</button>
                  )}
                </div>
              )}

              {confirming && (
                <ConfirmDialog title="Publish new message prices" confirmLabel="Publish prices" pending={save.isPending}
                  confirmDisabled={!reasonOk(reason) || (needsAllow && !allowBelow)}
                  onConfirm={() => save.mutate(changes.map((c) => {
                    const input: MessagePriceInput = { product: c.row.product, type: c.row.type, country: c.country, price_credits: c.price };
                    // Consent travels only on the prices it was given for — or on every price when billing found one we could not.
                    if (allowBelow && c.price !== null && (isBelowCost(c) || (serverSaysBelowCost && c.threshold === null))) {
                      input.allow_below_cost = true;
                    }
                    return input;
                  }))}
                  onCancel={() => { setConfirming(false); save.reset(); }}>
                  <ul className="t-body" style={{ margin: 0, paddingInlineStart: 18 }}>
                    {changes.map((c) => (
                      <li key={c.key}>
                        {PRODUCT_TEXT[c.row.product].title} {rowName(c.row)}{c.country === '*' ? ' (every country)' : ` in ${c.country}`}:{' '}
                        {c.before === null ? 'not priced' : formatCredits(c.before, scale)} →{' '}
                        {c.price === null
                          ? <strong>cost + markup (the price-book row is removed)</strong>
                          : <><strong>{formatCredits(c.price, scale)}</strong> {unitText(c.row)}</>}
                        {isBelowCost(c) && (
                          <span style={{ color: 'var(--error)' }}> — below our cost ({formatCredits(c.threshold, scale)}{c.country === '*' ? ', the highest country it covers' : ''})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="t-caption muted">Applies to every tenant from now on, except tenants with their own price.</p>
                  {needsAllow && (
                    <div className="callout callout-warning" role="status">
                      <span className="callout-icon" aria-hidden="true">!</span>
                      <div className="t-body">
                        <strong className="t-heading" style={{ display: 'block' }}>
                          {belowCost.length > 0
                            ? `${belowCost.length} price${belowCost.length === 1 ? ' is' : 's are'} below our cost`
                            : 'Billing says a price is below our cost (PRICE_BELOW_COST)'}
                        </strong>
                        Every message sent at that price loses money. Billing refuses it unless you allow it explicitly.
                        <CheckboxField label="Allow below cost — I accept the loss" checked={allowBelow} onChange={setAllowBelow} />
                      </div>
                    </div>
                  )}
                  <ReasonField value={reason} onChange={setReason} />
                  <MutationError error={save.error} service="billing" />
                </ConfirmDialog>
              )}
            </div>
          );
        }}
      </QueryState>
    </RequirePermission>
  );
}

/** A type's use in the rated month: the line(s) of group `<product>.<type>`, in the type's own unit. */
function usageOf(lines: RatedLine[], product: Product, row: MessagePriceRow) {
  const mine = lines.filter((l) => l.group === `${product}.${row.type}`);
  return {
    quantity: mine.filter((l) => l.unit === row.unit).reduce((sum, l) => sum + l.quantity, 0),
    credits: mine.reduce((sum, l) => sum + l.credits, 0),
    cost: mine.reduce((sum, l) => sum + l.cost_credits, 0),
    margin: mine.reduce((sum, l) => sum + l.margin_credits, 0),
  };
}

/**
 * The tenant page ▸ Messaging: what this tenant pays per SMS / WhatsApp type (its own prices applied, per country too) beside
 * what it sent in a month, what that was charged, our cost and the margin. Both reads are live billing calls (CP2).
 */
export function TenantMessagePricesSection({ env, tenantId }: { env: string; tenantId: string }) {
  const [period, setPeriod] = useState(currentPeriod());
  // Both reads are costs:read: gated BEFORE they fire, so a viewer's visit sends no refused calls.
  const seesCosts = hasPermission(PERMISSIONS.costsRead);
  const prices = useQuery({
    queryKey: pricesKey(env, tenantId),
    queryFn: () => billingApi.messagePrices(env, tenantId),
    enabled: seesCosts,
    ...LIVE,
  });
  const rating = useQuery({
    queryKey: ['ops', 'billing', env, 'rating', tenantId, period],
    queryFn: () => billingApi.rating(env, tenantId, period),
    enabled: seesCosts,
    ...LIVE,
  });

  if (!seesCosts) return null;

  return (
    <Section title="Prices and use by message type" meta="this tenant's prices; use, charges, cost and margin for the month"
      actions={<MonthSelect value={period} onChange={setPeriod} />} flush>
      <QueryState query={prices} service="billing">
        {(data: MessagePrices) => (
          <QueryState query={rating} service="billing">
            {(usage) => (
              <div className="stack" style={{ gap: 0 }}>
                {(['sms', 'whatsapp'] as const).map((product) => (
                  <div className="table-wrap" key={product}>
                    <table className="table">
                      <caption className="t-label" style={{ textAlign: 'start', padding: '12px 16px 4px' }}>{PRODUCT_TEXT[product].title}</caption>
                      <thead>
                        <tr>
                          <th>Message type</th>
                          <th className="num">Price ({PRODUCT_TEXT[product].unit})</th>
                          <th className="num">{product === 'sms' ? 'Parts sent' : 'Messages sent'}</th>
                          <th className="num">Charged</th>
                          <th className="num">Our cost</th>
                          <th className="num">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(product === 'sms' ? data.sms : data.whatsapp).map((row) => {
                          const used = usageOf(usage.lines, product, row);
                          return (
                            <tr key={row.sku}>
                              <td>
                                <div style={{ fontWeight: 600 }}>{rowName(row)}</div>
                                {row.country_prices.length > 0 && (
                                  <div className="t-caption muted">
                                    {row.country_prices.map((c) => `${c.country}: ${c.price_credits === null ? 'not priced' : formatCredits(c.price_credits, data.credit_display_scale)}${c.source === 'override' ? ' (own)' : ''}`).join(' · ')}
                                  </div>
                                )}
                              </td>
                              <td className="num">
                                <div>{row.price_credits === null ? '—' : formatCredits(row.price_credits, data.credit_display_scale)}</div>
                                <SourceChip source={row.source} />
                              </td>
                              <td className="num">{formatNumber(used.quantity)}</td>
                              <td className="num">{formatCredits(used.credits, usage.credit_display_scale)}</td>
                              <td className="num">{formatCredits(used.cost, usage.credit_display_scale)}</td>
                              <td className="num" style={used.margin < 0 ? { color: 'var(--error)', fontWeight: 600 } : undefined}>
                                {formatCredits(used.margin, usage.credit_display_scale)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
                <p className="t-caption muted" style={{ margin: 0, padding: '8px 16px 12px' }}>
                  {PRODUCT_TEXT.sms.note} Change a price for every tenant under Pricing ▸ Message prices, or for this tenant only
                  with a price override on the Billing tab.
                </p>
              </div>
            )}
          </QueryState>
        )}
      </QueryState>
    </Section>
  );
}
